<template>
  <div class="admin-panel">
    <div v-if="!authed" class="login">
      <h2>Admin login</h2>
      <input type="password" v-model="pass" placeholder="password" />
        <button class="btn primary" @click="tryLogin">Login</button>
    </div>

    <div v-else class="controls">
      <div class="admin-header">
        <h2>Gallery Admin</h2>
        <div class="controls-top">
          <div class="uploader">
            <label class="file-label">
              <input type="file" ref="file" @change="onFileChange" accept="image/*,.heic,.heif" />
              <span>Select file</span>
            </label>
            <span class="filename">{{ selectedFileName || 'no file selected' }}</span>
            <input v-model="subdir" placeholder="optional subfolder (e.g. Spain2024)" />
            <button class="btn" :disabled="!selectedFile || loading || !apiAvailable" @click="upload">Upload</button>
          </div>
          <button class="btn danger" @click="logout">Logout</button>
        </div>
      </div>

      <div class="images">
        <div v-if="!apiAvailable" class="offline-notice">
          Admin API not reachable — uploads are disabled. Changes will be local-only until you save and commit.
          <button class="btn" @click="downloadIndex" style="margin-left:8px">Download index.json</button>
        </div>
        <div class="images-header">
          <h3>Images</h3>
          <button class="btn primary" @click="saveOrder" :disabled="loading">Save order</button>
        </div>
        <div class="images-list">
            <div
              v-for="(item, idx) in allItems"
              :key="item.id"
              class="image-row tile"
              :class="{ dragging: dragIndex === idx, 'is-group': item.type === 'group' }"
              draggable="true"
              @dragstart="dragStart(idx)"
              @dragover.prevent="dragOver(idx)"
              @drop="drop(idx)"
            >
              <div class="img-wrap">
                <img v-if="item.type === 'image'" :src="item.value" />
                <div v-else class="group-thumb">📁 {{ item.value }}</div>
                <button v-if="item.type === 'image'" class="delete-x" @click="deleteImage(item.value)" title="Delete">×</button>
              </div>
              <div class="meta">
                <div class="path">{{ item.type === 'group' ? `Folder: ${item.value}` : item.value }}</div>
                <div class="actions">
                  <button class="btn small" @click="moveUp(idx)" :disabled="idx===0">▲</button>
                  <button class="btn small" @click="moveDown(idx)" :disabled="idx===allItems.length-1">▼</button>
                </div>
              </div>
            </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      pass: '',
      authed: false,
      gallery: { images: [], groups: {} },
      subdir: '',
      selectedFile: null,
      selectedFileName: '',
      loading: false,
      apiAvailable: false,
      dirty: false,
      dragIndex: null,
      dragOverIndex: null,
      apiBaseUrl: null
    }
  },
  computed: {
    allItems() {
      const items = []
      const groups = this.gallery.groups || {}
      const images = this.gallery.images || []
      const groupOrder = this.gallery.groupOrder || Object.keys(groups)
      const itemOrder = Array.isArray(this.gallery.itemOrder) ? this.gallery.itemOrder : null
      const seenGroups = new Set()
      const seenImages = new Set()

      if (itemOrder) {
        for (const token of itemOrder) {
          if (typeof token !== 'string') continue
          if (token.startsWith('group:')) {
            const groupName = token.slice('group:'.length)
            if (groups[groupName] && !seenGroups.has(groupName)) {
              items.push({ type: 'group', value: groupName, id: `group-${groupName}` })
              seenGroups.add(groupName)
            }
            continue
          }
          if (token.startsWith('image:')) {
            const imagePath = token.slice('image:'.length)
            if (images.includes(imagePath) && !seenImages.has(imagePath)) {
              items.push({ type: 'image', value: imagePath, id: imagePath })
              seenImages.add(imagePath)
            }
          }
        }
      }

      for (const groupName of groupOrder) {
        if (groups[groupName] && !seenGroups.has(groupName)) {
          items.push({ type: 'group', value: groupName, id: `group-${groupName}` })
          seenGroups.add(groupName)
        }
      }

      for (const img of images) {
        if (!seenImages.has(img)) {
          items.push({ type: 'image', value: img, id: img })
          seenImages.add(img)
        }
      }

      return items
    }
  },
  mounted() {
    this.apiBaseUrl = `http://${window.location.hostname}:4000`
    this.fetchGallery()
    this.checkApi()
  },
  methods: {
    async checkApi() {
      try {
        const r = await fetch(`${this.apiBaseUrl}/api/gallery`, { method: 'GET' })
        this.apiAvailable = r.ok
      } catch (e) {
        this.apiAvailable = false
      }
    },
    async fetchGallery() {
      try {
        const rr = await fetch(`${this.apiBaseUrl}/api/gallery`)
        if (rr.ok) {
          this.gallery = await rr.json()
          return
        }
        const r = await fetch(`/photos/index.json?t=${Date.now()}`)
        if (r.ok) this.gallery = await r.json()
      } catch (e) { console.warn(e) }
    },
    tryLogin() {
      this.authed = !!this.pass
    },
    async upload() {
      if (!this.apiAvailable) return alert('Upload unavailable: admin API not reachable. Add files to public/photos and run the generator or start the admin server.')
      const f = this.selectedFile || (this.$refs.file && this.$refs.file.files[0])
      if (!f) return alert('pick a file')
      this.loading = true
      try {
        const fd = new FormData()
        fd.append('file', f, f.name)
        if (this.subdir) fd.append('subdir', this.subdir)
        const res = await fetch(`${this.apiBaseUrl}/api/upload`, { method: 'POST', body: fd, headers: { 'x-admin-pass': this.pass } })
        if (res.ok) this.gallery = await res.json()
        else alert('upload failed')
      } catch (e) { alert('upload error') }
      this.loading = false
    },
    onFileChange(e) {
      const f = e.target.files && e.target.files[0]
      if (f) {
        this.selectedFile = f
        this.selectedFileName = f.name
      } else {
        this.selectedFile = null
        this.selectedFileName = ''
      }
    },
    dragStart(i) {
      this.dragIndex = i
    },
    dragOver(i) {
      this.dragOverIndex = i
    },
    drop(i) {
      const from = this.dragIndex
      const to = i
      if (from == null) return
      if (from === to) { this.dragIndex = null; this.dragOverIndex = null; return }
      
      const itemsCopy = this.allItems.slice()
      const movedItem = itemsCopy.splice(from, 1)[0]
      itemsCopy.splice(to, 0, movedItem)
      
      // Reconstruct gallery from the reordered items
      const newImages = []
      const newGroupOrder = []
      const newItemOrder = []
      for (const item of itemsCopy) {
        if (item.type === 'image') {
          newImages.push(item.value)
          newItemOrder.push(`image:${item.value}`)
        } else {
          newGroupOrder.push(item.value)
          newItemOrder.push(`group:${item.value}`)
        }
      }
      
      this.gallery.images = newImages
      this.gallery.groupOrder = newGroupOrder
      this.gallery.itemOrder = newItemOrder
      
      this.dragIndex = null
      this.dragOverIndex = null
    },
    async deleteImage(path) {
      if (!confirm('Delete ' + path + '?')) return
      if (this.apiAvailable) {
        this.loading = true
        try {
          console.log('About to delete:', path)
          const res = await fetch(`${this.apiBaseUrl}/api/delete`, { method: 'POST', headers: { 'Content-Type':'application/json', 'x-admin-pass': this.pass }, body: JSON.stringify({ path }) })
          console.log('Response status:', res.status, 'ok:', res.ok)
          if (res.ok) {
            const data = await res.json()
            console.log('Delete response:', data)
            console.log('Before assignment, authed:', this.authed)
            this.gallery = data
            console.log('After assignment, authed:', this.authed)
            console.log('Gallery updated:', this.gallery)
          } else {
            const error = await res.text()
            console.error('Delete failed:', res.status, error)
            alert('delete failed: ' + res.status + ' ' + error)
          }
        } catch (e) { 
          console.error('Delete error:', e, e.stack)
          alert('delete error: ' + e.message) 
        }
        this.loading = false
      } else {
        this.gallery.images = this.gallery.images.filter(i => i !== path)
        this.dirty = true
      }
    },
    moveUp(i) {
      const items = this.allItems
      if (i===0) return
      const itemsCopy = items.slice()
      const t = itemsCopy[i-1]; itemsCopy[i-1]=itemsCopy[i]; itemsCopy[i]=t
      
      // Reconstruct gallery
      const newImages = []
      const newGroupOrder = []
      const newItemOrder = []
      for (const item of itemsCopy) {
        if (item.type === 'image') {
          newImages.push(item.value)
          newItemOrder.push(`image:${item.value}`)
        } else {
          newGroupOrder.push(item.value)
          newItemOrder.push(`group:${item.value}`)
        }
      }
      this.gallery.images = newImages
      this.gallery.groupOrder = newGroupOrder
      this.gallery.itemOrder = newItemOrder
    },
    moveDown(i) {
      const items = this.allItems
      if (i===items.length-1) return
      const itemsCopy = items.slice()
      const t = itemsCopy[i+1]; itemsCopy[i+1]=itemsCopy[i]; itemsCopy[i]=t
      
      // Reconstruct gallery
      const newImages = []
      const newGroupOrder = []
      const newItemOrder = []
      for (const item of itemsCopy) {
        if (item.type === 'image') {
          newImages.push(item.value)
          newItemOrder.push(`image:${item.value}`)
        } else {
          newGroupOrder.push(item.value)
          newItemOrder.push(`group:${item.value}`)
        }
      }
      this.gallery.images = newImages
      this.gallery.groupOrder = newGroupOrder
      this.gallery.itemOrder = newItemOrder
    },
    async saveOrder() {
      if (this.apiAvailable) {
        this.loading = true
        try {
          console.log('Saving order...')
          const res = await fetch(`${this.apiBaseUrl}/api/reorder`, { method: 'POST', headers: { 'Content-Type':'application/json', 'x-admin-pass': this.pass }, body: JSON.stringify({ images: this.gallery.images, groupOrder: this.gallery.groupOrder, itemOrder: this.gallery.itemOrder }) })
          console.log('Reorder response status:', res.status)
          if (res.ok) { 
            const data = await res.json()
            console.log('Reorder response:', data)
            this.gallery = data
            console.log('Gallery updated, authed:', this.authed)
            this.dirty = false 
          }
          else alert('save failed')
        } catch (e) { 
          console.error('Save error:', e)
          alert('save error') 
        }
        this.loading = false
      } else {
        this.downloadIndex()
      }
    },

    downloadIndex() {
      const data = JSON.stringify(this.gallery, null, 2)
      const blob = new Blob([data], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'index.json'
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
      alert('Downloaded index.json — place it at public/photos/index.json in your repo and commit/push to persist changes')
      this.dirty = false
    },
    logout() { this.authed = false; this.pass = '' }
  }
}
</script>

<style scoped>
.admin-panel { padding: 18px; font-family: system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial }
.admin-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:12px }
.controls-top { display:flex; gap:12px; align-items:center }
.uploader { display:flex; gap:8px; align-items:center }
.file-label { background:#f0f0f0; padding:8px 12px; border-radius:6px; cursor:pointer }
.file-label input { display:none }
.filename { color:#666; font-size:0.9rem; min-width:200px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap }
.images { margin-top:12px }
.images-header { display:flex; justify-content:space-between; align-items:center }
.images-list { max-height:60vh; overflow:auto; margin-top:8px; border:1px solid #eee; padding:8px; border-radius:6px; display:grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap:12px }
.offline-notice { background:#fff3cd; border:1px solid #ffc107; padding:10px 12px; border-radius:6px; margin-bottom:10px; font-size:0.9rem; color:#856404 }
.image-row { display:flex; gap:12px; align-items:center; padding:8px; border-radius:6px; background:#fff; box-shadow:0 0 0 1px rgba(0,0,0,0.02) }
.image-row.tile { flex-direction:column; align-items:stretch }
.image-row img { width:100%; height:140px; object-fit:cover; border-radius:4px }
.image-row.is-group { background:#f0f0f0; border:2px dashed #999 }
.group-thumb { width:100%; height:140px; display:flex; align-items:center; justify-content:center; font-size:3rem; background:#e8e8e8; border-radius:4px }
.image-row { position:relative }
.img-wrap { position:relative }
.delete-x { position:absolute; top:6px; right:6px; background:rgba(0,0,0,0.6); color:white; border:none; border-radius:50%; width:28px; height:28px; cursor:pointer; display:flex; align-items:center; justify-content:center }
.delete-x:hover { background:rgba(0,0,0,0.85) }
.image-row.dragging { opacity:0.6; transform:scale(0.995) }
.meta { display:flex; justify-content:space-between; align-items:center; width:100% }
.path { font-size:0.9rem; color:#333; overflow:hidden; text-overflow:ellipsis; white-space:nowrap }
.actions { display:flex; gap:6px }
.btn { background:#eee; border:none; padding:8px 10px; border-radius:6px; cursor:pointer }
.btn.primary { background:#1677ff; color:white }
.btn.danger { background:#ff4d4f; color:white }
.btn.small { padding:6px 8px; font-size:0.85rem }
.btn[disabled] { opacity:0.6; cursor:not-allowed }
</style>
